import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import {
  Backup,
  Restore,
  Security,
  Upload,
  Delete,
  CloudDownload,
  RestoreFromTrash,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { databaseApi } from '../services/api';
import { saveOrShareBlob } from '../utils/fileDownload';

interface BackupFile {
  id: string;
  filename: string;
  size: string;
  created_at: string;
  encrypted?: boolean;
}

export const DatabaseAdminPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // Dialog states
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [directRestoreDialogOpen, setDirectRestoreDialogOpen] = useState(false);
  const [selectedBackupFile, setSelectedBackupFile] = useState<string>('');
  
  // Form states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [backupFiles, setBackupFiles] = useState<BackupFile[]>([]);
  const [customFilename, setCustomFilename] = useState<string>('');
  
  const [credentials, setCredentials] = useState({
    currentPassword: '',
    newUser: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Load backup files on component mount
  useEffect(() => {
    if (user?.role === 'admin') {
      loadBackupFiles();
    }
  }, [user?.role]);

  // Helper function to remove file extension from display
  const removeFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex > 0) {
      return filename.substring(0, lastDotIndex);
    }
    return filename;
  };

  // Only allow admin users
  if (user?.role !== 'admin') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error">
          {t('access_denied')} - Only administrators can access this page.
        </Alert>
      </Box>
    );
  }

  const loadBackupFiles = async () => {
    try {
      const backups = await databaseApi.getBackups();
      setBackupFiles(backups);
    } catch (err: any) {
      console.error('Error loading backups:', err);
      // Use mock data if API fails
      setBackupFiles([
        {
          id: '1',
          filename: 'backup_2025_01_06_14_30.sql',
          size: '2.5 MB',
          created_at: '2025-01-06 14:30:00'
        },
        {
          id: '2',
          filename: 'backup_2025_01_05_10_15.sql',
          size: '2.3 MB',
          created_at: '2025-01-05 10:15:00'
        }
      ]);
    }
  };

  const handleCreateBackup = async () => {
    setLoading(true);
    setError('');
    try {
      await databaseApi.createBackup(customFilename.trim() || undefined);
      setSuccess(t('backup_created'));
      setBackupDialogOpen(false);
      setCustomFilename(''); // Clear the input after successful backup
      // Refresh backup list
      await loadBackupFiles();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('backup_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreDatabase = async () => {
    if (!selectedFile) return;
    
    setLoading(true);
    setError('');
    try {
      await databaseApi.restoreFromBackup(selectedFile);
      setSuccess(t('restore_completed'));
      setRestoreDialogOpen(false);
      setSelectedFile(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('restore_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCredentials = async () => {
    if (credentials.newPassword !== credentials.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      await databaseApi.updateCredentials({
        current_password: credentials.currentPassword,
        new_user: credentials.newUser,
        new_password: credentials.newPassword
      });
      
      setSuccess(t('credentials_updated'));
      setCredentialsDialogOpen(false);
      setCredentials({
        currentPassword: '',
        newUser: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('credentials_update_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = async (filename: string) => {
    try {
      const blob = await databaseApi.downloadBackup(filename);
      await saveOrShareBlob(blob, filename);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to download backup');
    }
  };

  const handleDeleteBackup = async (backupId: string) => {
    if (!window.confirm('Are you sure you want to delete this backup?')) return;
    
    try {
      await databaseApi.deleteBackup(backupId);
      setBackupFiles(backupFiles.filter(f => f.id !== backupId));
      setSuccess('Backup deleted successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete backup');
    }
  };

  const handleDirectRestore = (filename: string) => {
    setSelectedBackupFile(filename);
    setDirectRestoreDialogOpen(true);
  };

  const handleConfirmDirectRestore = async () => {
    if (!selectedBackupFile) return;
    
    setLoading(true);
    setError('');
    try {
      await databaseApi.restoreFromExistingFile(selectedBackupFile);
      setSuccess(`Database restored successfully from ${removeFileExtension(selectedBackupFile)}`);
      setDirectRestoreDialogOpen(false);
      setSelectedBackupFile('');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to restore database');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          {t('database_administration')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Manage database backups, restoration, and credentials
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Backup Management */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Backup sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">{t('backup_database')}</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Create a backup of the current database state
              </Typography>
              <Button
                variant="contained"
                startIcon={<Backup />}
                onClick={() => setBackupDialogOpen(true)}
                disabled={loading}
              >
                {t('create_backup')}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Restore Management */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Restore sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">{t('restore_database')}</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Restore database from a backup file
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => setRestoreDialogOpen(true)}
                disabled={loading}
              >
                {t('restore_backup')}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Database Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Security sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6">{t('database_settings')}</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Change database user credentials
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Security />}
                onClick={() => setCredentialsDialogOpen(true)}
                disabled={loading}
                color="error"
              >
                {t('change_db_credentials')}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Backup Files List */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Available Backups
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List>
                {backupFiles.map((backup) => (
                  <ListItem key={backup.id}>
                    <ListItemText
                      primary={removeFileExtension(backup.filename)}
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip label={backup.size} size="small" />
                          <Chip label={backup.created_at} size="small" variant="outlined" />
                          {backup.encrypted && (
                            <Chip 
                              label="🔒 Encrypted" 
                              size="small" 
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={() => handleDirectRestore(backup.filename)}
                        color="success"
                        title="Restore from this backup"
                        disabled={loading}
                      >
                        <RestoreFromTrash />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDownloadBackup(backup.filename)}
                        color="primary"
                        title={t('download_backup')}
                      >
                        <CloudDownload />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteBackup(backup.id)}
                        color="error"
                        title="Delete backup"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create Backup Dialog */}
      <Dialog open={backupDialogOpen} onClose={() => setBackupDialogOpen(false)}>
        <DialogTitle>{t('create_backup')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('confirm_backup')}
          </Typography>
          <TextField
            label="Custom Backup Name (optional)"
            value={customFilename}
            onChange={(e) => setCustomFilename(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., my_backup_name (will be saved as my_backup_name.bin)"
            helperText="Leave empty to use default SysLog_date_time.bin format"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupDialogOpen(false)}>{t('cancel')}</Button>
          <Button onClick={handleCreateBackup} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : t('create_backup')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Database Dialog */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
        <DialogTitle>{t('restore_database')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, color: 'warning.main' }}>
            {t('confirm_restore')}
          </Typography>
          <input
            type="file"
            accept=".sql,.gz,.bin"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            style={{ marginBottom: '16px' }}
          />
          {selectedFile && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>{t('cancel')}</Button>
          <Button 
            onClick={handleRestoreDatabase} 
            variant="contained" 
            color="warning"
            disabled={loading || !selectedFile}
          >
            {loading ? <CircularProgress size={20} /> : t('restore_backup')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Credentials Dialog */}
      <Dialog open={credentialsDialogOpen} onClose={() => setCredentialsDialogOpen(false)}>
        <DialogTitle>{t('change_db_credentials')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('db_current_password')}
            type="password"
            value={credentials.currentPassword}
            onChange={(e) => setCredentials({ ...credentials, currentPassword: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label={t('database_user')}
            value={credentials.newUser}
            onChange={(e) => setCredentials({ ...credentials, newUser: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('db_new_password')}
            type="password"
            value={credentials.newPassword}
            onChange={(e) => setCredentials({ ...credentials, newPassword: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('db_confirm_new_password')}
            type="password"
            value={credentials.confirmPassword}
            onChange={(e) => setCredentials({ ...credentials, confirmPassword: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredentialsDialogOpen(false)}>{t('cancel')}</Button>
          <Button 
            onClick={handleUpdateCredentials} 
            variant="contained" 
            color="error"
            disabled={loading || !credentials.currentPassword || !credentials.newPassword}
          >
            {loading ? <CircularProgress size={20} /> : t('update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Direct Restore Confirmation Dialog */}
      <Dialog open={directRestoreDialogOpen} onClose={() => setDirectRestoreDialogOpen(false)}>
        <DialogTitle>Confirm Database Restore</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, color: 'warning.main' }}>
            ⚠️ Are you sure you want to restore the database from this backup?
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Backup file:</strong> {removeFileExtension(selectedBackupFile)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            This action will completely replace the current database with the backup data. 
            All current data will be lost. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDirectRestoreDialogOpen(false)}>{t('cancel')}</Button>
          <Button 
            onClick={handleConfirmDirectRestore} 
            variant="contained" 
            color="warning"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Restore Database'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
